import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { bannersAPI } from "../services/api";
import "../styles/ManageBanners.css";

const ManageBanners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    icon: "rocket_launch_rounded",
    color1: "#4FC3F7",
    color2: "#B39DDB",
    action_url: "",
    is_active: 1,
    sort_order: 0,
  });

  // 🎨 قوالب جاهزة للألوان (أسهل بكثير للمستخدم من اختيار أكواد Hex)
  const colorPresets = [
    { name: "أزرق سماوي", c1: "#4FC3F7", c2: "#B39DDB" },
    { name: "برتقالي دافئ", c1: "#FFb74D", c2: "#FF8A65" },
    { name: "أخضر زمردي", c1: "#4DB6AC", c2: "#81C784" },
    { name: "أرجواني ليلي", c1: "#7E57C2", c2: "#5C6BC0" },
    { name: "وردي ناعم", c1: "#F06292", c2: "#FF8A80" },
    { name: "غامق احترافي", c1: "#455A64", c2: "#263238" },
  ];

  const availableIcons = [
    { value: "rocket_launch_rounded", label: "صاروخ 🚀", icon: "rocket_launch" },
    { value: "emoji_events_rounded", label: "كأس 🏆", icon: "emoji_events" },
    { value: "local_offer_rounded", label: "خصم 🏷️", icon: "local_offer" },
    { value: "campaign_rounded", label: "إعلان 📢", icon: "campaign" },
    { value: "star_rounded", label: "نجمة ⭐", icon: "star" },
    { value: "info_rounded", label: "معلومات ℹ️", icon: "info" },
  ];

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const res = await bannersAPI.getAll();
      setBanners(res.data);
    } catch (err) {
      toast.error("فشل في جلب البانرات");
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingBanner(null);
    setFormData({
      title: "",
      subtitle: "",
      icon: "rocket_launch_rounded",
      color1: "#4FC3F7",
      color2: "#B39DDB",
      action_url: "",
      is_active: 1,
      sort_order: 0,
    });
    setShowModal(true);
  };

  const openEditModal = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title || "",
      subtitle: banner.subtitle || "",
      icon: banner.icon || "rocket_launch_rounded",
      color1: banner.color1 || "#4FC3F7",
      color2: banner.color2 || "#B39DDB",
      action_url: banner.action_url || "",
      is_active: banner.is_active,
      sort_order: banner.sort_order || 0,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا البانر؟")) return;
    try {
      await bannersAPI.delete(id);
      toast.success("تم الحذف بنجاح");
      fetchBanners();
    } catch (err) {
      toast.error("فشل الحذف");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSave = { ...formData, is_active: parseInt(formData.is_active) };
      
      if (editingBanner) {
        await bannersAPI.update(editingBanner.id, dataToSave);
        toast.success("تم التحديث بنجاح");
      } else {
        await bannersAPI.create(dataToSave);
        toast.success("تمت الإضافة بنجاح");
      }
      setShowModal(false);
      fetchBanners();
    } catch (err) {
      toast.error(err.response?.data?.message || "حدث خطأ أثناء الحفظ");
    }
  };

  const toggleStatus = async (banner) => {
    try {
      await bannersAPI.update(banner.id, {
        ...banner,
        is_active: banner.is_active ? 0 : 1,
      });
      toast.success("تم التحديث");
      fetchBanners();
    } catch (err) {
      toast.error("فشل تغيير الحالة");
    }
  };

  if (loading) {
    return <div className="loading-spinner">⏳ عرض البانرات...</div>;
  }

  return (
    <div className="manage-banners-page">
      <div className="banners-header">
        <h2>إدارة البانرات (الصفحة الرئيسية)</h2>
        <button className="add-btn" onClick={openAddModal}>
          + إضافة بانر جديد
        </button>
      </div>

      <p className="banners-hint">
        هذه البانرات تظهر في "الكاروسيل" في أعلى شاشة التطبيق للطلاب. يمكنك تفعيلها، تعطيلها، وترتيبها.
      </p>

      {banners.length === 0 ? (
        <div className="empty-state">لا توجد بانرات. أضف واحداً الآن.</div>
      ) : (
        <div className="banners-grid">
          {banners.map((banner) => (
            <div key={banner.id} className={`banner-card ${banner.is_active ? "" : "disabled"}`}>
              <div
                className="banner-preview"
                style={{
                  background: `linear-gradient(135deg, ${banner.color1}, ${banner.color2})`,
                }}
              >
                <div className="banner-icon-bg">
                  {/* Just a placeholder symbol to visually represent the icon name */}
                  <span className="material-icons-outlined" style={{ fontFamily: 'Material Symbols Outlined' }}>
                    {banner.icon}
                  </span>
                </div>
                <div className="banner-content">
                  <h3>{banner.title}</h3>
                  <p>{banner.subtitle}</p>
                </div>
              </div>

              <div className="banner-actions">
                <span className="order-badge">ترتيب: {banner.sort_order}</span>
                {banner.action_url && <span className="action-badge" title={banner.action_url}>🔗 رابط مرفق</span>}
                <div className="actions-right">
                  <button
                    className={`status-btn ${banner.is_active ? "active" : "inactive"}`}
                    onClick={() => toggleStatus(banner)}
                    title={banner.is_active ? "تعطيل" : "تفعيل"}
                  >
                    {banner.is_active ? "✅ مفعل" : "❌ معطل"}
                  </button>
                  <button className="edit-btn" onClick={() => openEditModal(banner)}>
                    ✏️ تعديل
                  </button>
                  <button className="delete-btn" onClick={() => handleDelete(banner.id)}>
                    🗑️ حذف
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content banners-modal">
            <h3>{editingBanner ? "تعديل البانر" : "إضافة بانر جديد"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>العنوان</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="مثال: خصم 50% اليوم فقط!"
                />
              </div>

              <div className="form-group">
                <label>النص الفرعي</label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  placeholder="الوصف أسفل العنوان"
                />
              </div>

              <div className="form-group">
                <label>رابط التوجيه (اختياري)</label>
                <input
                  type="url"
                  value={formData.action_url}
                  onChange={(e) => setFormData({ ...formData, action_url: e.target.value })}
                  placeholder="مثال: https://daress.app/discount"
                />
                <small className="hint-text">إذا أدخلت رابطاً، سيتم فتح هذا الرابط عندما يضغط الطالب على البانر في التطبيق.</small>
              </div>

              <div className="form-group">
                <label>الأيقونة</label>
                <div className="icon-selector">
                  {availableIcons.map((i) => (
                    <div 
                      key={i.value} 
                      className={`icon-option ${formData.icon === i.value ? 'selected' : ''}`}
                      onClick={() => setFormData({ ...formData, icon: i.value })}
                    >
                      <span className="material-icons-outlined" style={{fontFamily: 'Material Symbols Outlined'}}>{i.icon}</span>
                      <span>{i.label.split(' ')[0]}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>اختر لون البانر</label>
                <div className="color-presets">
                  {colorPresets.map((preset, idx) => (
                    <div 
                      key={idx}
                      className={`color-preset ${formData.color1 === preset.c1 ? 'selected' : ''}`}
                      onClick={() => setFormData({ ...formData, color1: preset.c1, color2: preset.c2 })}
                      title={preset.name}
                      style={{ background: `linear-gradient(135deg, ${preset.c1}, ${preset.c2})` }}
                    >
                      {formData.color1 === preset.c1 && <span className="check-mark">✓</span>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>الترتيب (الأصغر يظهر أولاً)</label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  style={{ maxWidth: '100px' }}
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active === 1}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })
                    }
                  />
                  تفعيل البانر ويظهر للمستخدمين مباشرة
                </label>
              </div>

              {/* Live Preview inside Modal */}
              <div className="live-preview">
                <p className="preview-label">شكل البانر بالتطبيق:</p>
                <div
                  className="banner-preview"
                  style={{ background: `linear-gradient(135deg, ${formData.color1}, ${formData.color2})` }}
                >
                  <div className="banner-icon-bg">
                    <span className="material-icons-outlined" style={{ fontFamily: 'Material Symbols Outlined' }}>
                      {formData.icon}
                    </span>
                  </div>
                  <div className="banner-content">
                    <h3>{formData.title || "عنوان البانر"}</h3>
                    <p>{formData.subtitle || "النص الفرعي يظهر هنا"}</p>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowModal(false)}>
                  إلغاء
                </button>
                <button type="submit" className="save-btn">
                  حفظ
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageBanners;
